import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import oatTheme from './theme/muiTheme';
import { AuthProvider } from './contexts/AuthContext';
import ClientList from './components/client/ClientList';
import ClientForm from './components/client/ClientForm';
import StrategyList from './components/StrategyList';
import StrategyDetail from './components/StrategyDetail';
import StrategyForm from './components/StrategyForm';
import SchemeList from './components/SchemeList';
import SchemeDetail from './components/SchemeDetail';
import CompanyList from './components/CompanyList';
import CompanyDetail from './components/CompanyDetail';
import CompanyForm from './components/CompanyForm';
import HoldingsList from './components/HoldingsList';
import HoldingsDetail from './components/HoldingsDetail';
import SchemeWiseHoldings from './components/SchemeWiseHoldings';
import ContractNotesList from './components/ContractNotesList';
import ContractNotesDetail from './components/ContractNotesDetail';
import CustodyHoldingsList from './components/CustodyHoldingsList';
import SoaList from './components/soa/SoaList';
import CustodyHoldingsDetail from './components/CustodyHoldingsDetail';
import BankStatementList from './components/BankStatementList';
import OrdersList from './components/OrdersList';
import BuyOrderForm from './components/BuyOrderForm';
import SellOrderForm from './components/SellOrderForm';
import OrderExecutionDashboard from './components/order_execution_tracker/OrderExecutionDashboard';
import BlockTradeList from './components/block_trade/BlockTradeList';
import BlockTradeDetail from './components/block_trade/BlockTradeDetail';
import ClientCash from './components/ClientCash';
import SchemeCash from './components/SchemeCash';
import TradeSuggestionsList from './components/trade_suggestions/TradeSuggestionsList';
import TradeSuggestionsDetail from './components/trade_suggestions/TradeSuggestionsDetail';
import Login from './components/Login';
import PrivateRoute from './components/PrivateRoute';
import Header from './components/Header';
import AllNotifications from './components/AllNotifications';
import FAHoldingsProcess from './components/FAHoldingsProcess';
import CustodyHoldingsProcess from './components/CustodyHoldingsProcess';
import './App.css';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import MarginCalculation from "@/components/MarginCalculation";
import PortfolioPerformance from "@/components/portfolio_performance/PortfolioPerformance";
import PortfolioTransaction from "@/components/PortfolioTransactions";
import VolumePage from "./components/VolumePage";
import ExecutionAnalyticsDashboard from './components/ExecutionAnalyticsDashboard';
import BrokerExecutionComparison from './components/BrokerComparison';
import BrokerWiseBrokerage from './components/BrokerWiseBrokerage';
import DistributorContribution from "@/components/DistributorContribution";
import MarketDataStaleness from "@/components/MarketDataStaleness";
import CashEnvironment from "@/components/CashEnvironment";
import StockwisePerformance from "@/components/StockwisePerformance";
import ClientPerformance from "@/components/client_performance/ClientPerformance";
import { NotificationProvider, NotificationRouteRefresher } from "@/contexts/NotificationContext";
import RiskCalculation from './components/RiskCalculation';
import SectorWeightPage from './components/SectorWeightPage';
import PortfolioPE from './components/portfolio_pe/PortfolioPE';
import ContributionAnalysis from './components/ContributionAnalysis';
import ApprovalReviewWrapper from './components/client/ApprovalReviewWrapper';
import PendingApprovalsList from "@/components/client/PendingApprovalsList";
import MasterManager from './components/masters/MasterManager';
import SellValidation from "@/components/SellValidation";
import LaunchDarklyWrapper from './components/utils/LaunchDarklyWrapper';
import ContractNoteProcess from "@/components/ContractNoteProcess";
import ContractFilesManage from "@/components/ContractFilesManage";
import KiteConnect from './components/KiteConnect';
import OrderAllocationList from './components/OrderAllocationList';
import OrderGenerationFailures from './components/OrderGenerationFailures';
import EtfOrderForm from './components/etf/EtfOrderForm';
import EtfOrdersList from './components/etf/EtfOrdersList';
import FeatureFlag from './components/utils/FeatureFlag';
import { FEATURE_FLAGS } from './config/featureFlagKeys';
import ManagementFees from './components/ManagementFees';
import ManagementFeeDetail from './components/ManagementFeeDetail';
import RolesManagement from '@/components/roles/RolesManagement';
import OrderRecommendationList from '@/components/order_recommendation/OrderRecommendationList'


// Dashboard, ScopeGate, and Fallback Page
import Dashboard from './components/Dashboard';
import ScopeGate from './components/utils/ScopeGate';
import AccessDenied from './components/utils/AccessDenied';

// Scope Codes
import {
  READ_CLIENT_MASTER, WRITE_CLIENT_MASTER, READ_GENERAL_MASTERS,
  WRITE_GENERAL_MASTERS, ORDERS_MANAGEMENT, HOLDINGS_AND_DATA,
  FINANCIAL_MANAGEMENT, PORTFOLIO_TRANSACTIONS, FEES_AND_DISTRIBUTOR,
  PERFORMANCE, ANALYTICS, TRADE_SUGGESTIONS, KITE_CONNECT, USER_ROLE_MANAGEMENT,
  PORTFOLIO_QA, OPERATIONS_CHECKLIST, SOA_MANAGEMENT
} from '@/constants/scopeCodes';

import RoleForm from '@/components/roles/RoleForm';
import UserList from '@/components/users/UserList';
import UserForm from '@/components/users/UserForm';
import QAPage from '@/components/qa/QAPage';
import SettingsMaster from '@/components/settings/SettingsMaster';
import OperationsChecklistPage from '@/components/operations_checklist/OperationsChecklistPage';
import OperationsHistoryPage from '@/components/operations_checklist/OperationsHistoryPage';
import ChecklistDetailPage from '@/components/operations_checklist/ChecklistDetailPage';
import StepDetailPage from '@/components/operations_checklist/StepDetailPage';
function App() {
  return (
    <ThemeProvider theme={oatTheme}>
      <AuthProvider>
        <LaunchDarklyWrapper>
          <NotificationProvider>
            <Router>
              <NotificationRouteRefresher />
              <Routes>
                {/* Public routes */}
                <Route path="/login" element={<Login />} />

                {/* Protected routes */}
                <Route element={<PrivateRoute />}>
                  {/* Default Route -> Now points to Dashboard */}
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />

                  {/* Dashboard Route (No ScopeGate, open to all logged-in users) */}
                  <Route path="/dashboard" element={
                    <div className="App">
                      <Header />
                      <Dashboard />
                    </div>
                  } />

                  <Route path="/notifications" element={
                    <div className="App">
                      <Header />
                      <AllNotifications />
                    </div>
                  } />

                  {/* ── Client Master ────────────────────────────────────────── */}
                  <Route path="/clients" element={
                    <ScopeGate scope={[READ_CLIENT_MASTER, WRITE_CLIENT_MASTER]} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <ClientList />
                      </div>
                    </ScopeGate>
                  } />

                  <Route path="/client-approvals" element={
                    <ScopeGate scope={[READ_CLIENT_MASTER, WRITE_CLIENT_MASTER]} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <PendingApprovalsList />
                      </div>
                    </ScopeGate>
                  } />

                  <Route path="/client-approvals/:id" element={
                    <ScopeGate scope={[READ_CLIENT_MASTER, WRITE_CLIENT_MASTER]} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <ApprovalReviewWrapper />
                      </div>
                    </ScopeGate>
                  } />

                  <Route path="/client-master/new" element={
                    <ScopeGate scope={WRITE_CLIENT_MASTER} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <ClientForm />
                      </div>
                    </ScopeGate>
                  } />

                  <Route path="/client-master/:id" element={
                    <ScopeGate scope={[READ_CLIENT_MASTER, WRITE_CLIENT_MASTER]} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <ClientForm viewMode={true} />
                      </div>
                    </ScopeGate>
                  } />

                  <Route path="/client-master/:id/edit" element={
                    <ScopeGate scope={WRITE_CLIENT_MASTER} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <ClientForm />
                      </div>
                    </ScopeGate>
                  } />

                  {/* ── General Masters ──────────────────────────────────────── */}
                  <Route path="/strategies" element={
                    <ScopeGate scope={[READ_GENERAL_MASTERS, WRITE_GENERAL_MASTERS]} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <StrategyList />
                      </div>
                    </ScopeGate>
                  } />

                  <Route path="/strategies/new" element={
                    <ScopeGate scope={WRITE_GENERAL_MASTERS} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <StrategyForm />
                      </div>
                    </ScopeGate>
                  } />

                  <Route path="/strategies/:id" element={
                    <ScopeGate scope={[READ_GENERAL_MASTERS, WRITE_GENERAL_MASTERS]} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <StrategyDetail />
                      </div>
                    </ScopeGate>
                  } />

                  <Route path="/strategies/:id/edit" element={
                    <ScopeGate scope={WRITE_GENERAL_MASTERS} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <StrategyForm />
                      </div>
                    </ScopeGate>
                  } />

                  <Route path="/schemes" element={
                    <ScopeGate scope={[READ_GENERAL_MASTERS, WRITE_GENERAL_MASTERS]} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <SchemeList />
                      </div>
                    </ScopeGate>
                  } />

                  <Route path="/schemes/:id" element={
                    <ScopeGate scope={[READ_GENERAL_MASTERS, WRITE_GENERAL_MASTERS]} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <SchemeDetail />
                      </div>
                    </ScopeGate>
                  } />

                  <Route path="/schemes/:id/edit" element={
                    <ScopeGate scope={WRITE_GENERAL_MASTERS} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <SchemeDetail startInEdit />
                      </div>
                    </ScopeGate>
                  } />

                  <Route path="/companies" element={
                    <ScopeGate scope={[READ_GENERAL_MASTERS, WRITE_GENERAL_MASTERS]} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <CompanyList />
                      </div>
                    </ScopeGate>
                  } />

                  <Route path="/companies/new" element={
                    <ScopeGate scope={WRITE_GENERAL_MASTERS} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <CompanyForm />
                      </div>
                    </ScopeGate>
                  } />

                  <Route path="/companies/:id" element={
                    <ScopeGate scope={[READ_GENERAL_MASTERS, WRITE_GENERAL_MASTERS]} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <CompanyDetail />
                      </div>
                    </ScopeGate>
                  } />

                  <Route path="/companies/:id/edit" element={
                    <ScopeGate scope={WRITE_GENERAL_MASTERS} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <CompanyForm />
                      </div>
                    </ScopeGate>
                  } />

                  <Route path="/masters" element={
                    <ScopeGate scope={[READ_GENERAL_MASTERS, WRITE_GENERAL_MASTERS]} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <MasterManager />
                      </div>
                    </ScopeGate>
                  } />

                  {/* ── Analytics ────────────────────────────────────────────── */}
                  <Route path="/volume-monitor" element={
                    <ScopeGate scope={ANALYTICS} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <VolumePage />
                      </div>
                    </ScopeGate>
                  } />

                  <Route path="/execution-analytics" element={
                    <ScopeGate scope={ANALYTICS} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <ExecutionAnalyticsDashboard />
                      </div>
                    </ScopeGate>
                  } />

                  <Route path="/broker-comparison" element={
                    <ScopeGate scope={ANALYTICS} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <BrokerExecutionComparison />
                      </div>
                    </ScopeGate>
                  } />

                  <Route path="/broker-wise-brokerage" element={
                    <ScopeGate scope={ANALYTICS} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <BrokerWiseBrokerage />
                      </div>
                    </ScopeGate>
                  } />

                  <Route path="/sector-holdings" element={
                    <ScopeGate scope={ANALYTICS} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <SectorWeightPage />
                      </div>
                    </ScopeGate>
                  } />

                  <Route path="/portfolio-pe" element={
                    <ScopeGate scope={ANALYTICS} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <PortfolioPE />
                      </div>
                    </ScopeGate>
                  } />

                  <Route path="/contribution-analysis" element={
                    <ScopeGate scope={ANALYTICS} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <ContributionAnalysis />
                      </div>
                    </ScopeGate>
                  } />

                  <Route path="/risk-calculation" element={
                    <ScopeGate scope={ANALYTICS} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <RiskCalculation />
                      </div>
                    </ScopeGate>
                  } />

                  {/* ── Holdings & Data ──────────────────────────────────────── */}
                  <Route path="/holdings" element={
                    <ScopeGate scope={HOLDINGS_AND_DATA} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <HoldingsList />
                      </div>
                    </ScopeGate>
                  } />

                  <Route path="/holdings/client/:clientId" element={
                    <ScopeGate scope={HOLDINGS_AND_DATA} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <HoldingsDetail />
                      </div>
                    </ScopeGate>
                  } />

                  <Route path="/scheme-wise-holdings" element={
                    <ScopeGate scope={HOLDINGS_AND_DATA} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <SchemeWiseHoldings />
                      </div>
                    </ScopeGate>
                  } />

                  <Route path="/contract-notes" element={
                    <ScopeGate scope={HOLDINGS_AND_DATA} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <ContractNotesList />
                      </div>
                    </ScopeGate>
                  } />

                  <Route path="/contract-notes/:id" element={
                    <ScopeGate scope={HOLDINGS_AND_DATA} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <ContractNotesDetail />
                      </div>
                    </ScopeGate>
                  } />

                  <Route path="/fa-holdings-process" element={
                    <ScopeGate scope={HOLDINGS_AND_DATA} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <FAHoldingsProcess />
                      </div>
                    </ScopeGate>
                  } />

                  <Route path="/custody-process" element={
                    <ScopeGate scope={HOLDINGS_AND_DATA} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <CustodyHoldingsProcess />
                      </div>
                    </ScopeGate>
                  } />

                  <Route path="/custody-holdings" element={
                    <ScopeGate scope={HOLDINGS_AND_DATA} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <CustodyHoldingsList />
                      </div>
                    </ScopeGate>
                  } />

                  <Route path="/soa" element={
                    <ScopeGate scope={SOA_MANAGEMENT} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <SoaList />
                      </div>
                    </ScopeGate>
                  } />

                  <Route path="/custody-holdings/client/:clientId" element={
                    <ScopeGate scope={HOLDINGS_AND_DATA} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <CustodyHoldingsDetail />
                      </div>
                    </ScopeGate>
                  } />

                  <Route path="/bank-statements" element={
                    <ScopeGate scope={HOLDINGS_AND_DATA} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <BankStatementList />
                      </div>
                    </ScopeGate>
                  } />

                  <Route path="/contract-notes-process" element={
                    <ScopeGate scope={HOLDINGS_AND_DATA} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <ContractNoteProcess />
                      </div>
                    </ScopeGate>
                  } />

                  <Route path="/contract-files-manage" element={
                    <ScopeGate scope={HOLDINGS_AND_DATA} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <ContractFilesManage />
                      </div>
                    </ScopeGate>
                  } />

                  {/* ── Financial Management ─────────────────────────────────── */}
                  <Route path="/margin-calculation" element={
                    <ScopeGate scope={FINANCIAL_MANAGEMENT} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <MarginCalculation />
                      </div>
                    </ScopeGate>
                  } />

                  <Route path="/client-cash" element={
                    <ScopeGate scope={FINANCIAL_MANAGEMENT} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <ClientCash />
                      </div>
                    </ScopeGate>
                  } />

                  <Route path="/scheme-cash" element={
                    <ScopeGate scope={FINANCIAL_MANAGEMENT} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <SchemeCash />
                      </div>
                    </ScopeGate>
                  } />

                  <Route path="/cash-environment" element={
                    <ScopeGate scope={FINANCIAL_MANAGEMENT} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <CashEnvironment />
                      </div>
                    </ScopeGate>
                  } />

                  <Route path="/management-fees" element={
                    <ScopeGate scope={FEES_AND_DISTRIBUTOR} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <ManagementFees />
                      </div>
                    </ScopeGate>
                  } />

                  <Route path="/management-fees/:clientCode" element={
                    <ScopeGate scope={FEES_AND_DISTRIBUTOR} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <ManagementFeeDetail />
                      </div>
                    </ScopeGate>
                  } />

                  <Route path="/sell/validation" element={
                    <ScopeGate scope={FINANCIAL_MANAGEMENT} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <SellValidation />
                      </div>
                    </ScopeGate>
                  } />

                  {/* ── Orders Management ────────────────────────────────────── */}
                  <Route path="/orders" element={
                    <ScopeGate scope={ORDERS_MANAGEMENT} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <OrdersList />
                      </div>
                    </ScopeGate>
                  } />

                  <Route path="/orders/buy/new" element={
                    <ScopeGate scope={ORDERS_MANAGEMENT} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <BuyOrderForm />
                      </div>
                    </ScopeGate>
                  } />

                  <Route path="/orders/sell/new" element={
                    <ScopeGate scope={ORDERS_MANAGEMENT} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <SellOrderForm />
                      </div>
                    </ScopeGate>
                  } />
                  {/* ETF order routes */}
                  <Route path="/etf-orders" element={
                    <div className="App">
                      <Header />
                      <EtfOrdersList />
                    </div>
                  } />
                  <Route path="/etf-orders/buy/new" element={
                    <ScopeGate scope={ORDERS_MANAGEMENT} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <EtfOrderForm type="buy" />
                      </div>
                    </ScopeGate>
                  } />

                  <Route path="/etf-orders/sell/new" element={
                    <ScopeGate scope={ORDERS_MANAGEMENT} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <EtfOrderForm type="sell" />
                      </div>
                    </ScopeGate>
                  } />

                  <Route path="/execution-board" element={
                    <ScopeGate scope={ORDERS_MANAGEMENT} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <OrderExecutionDashboard />
                      </div>
                    </ScopeGate>
                  } />

                  <Route path="/order-allocations" element={
                    <ScopeGate scope={ORDERS_MANAGEMENT} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <OrderAllocationList />
                      </div>
                    </ScopeGate>
                  } />

                  <Route path="/block-trades" element={
                    <ScopeGate scope={ORDERS_MANAGEMENT} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <BlockTradeList />
                      </div>
                    </ScopeGate>
                  } />

                  <Route path="/block-trades/:id" element={
                    <ScopeGate scope={ORDERS_MANAGEMENT} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <BlockTradeDetail />
                      </div>
                    </ScopeGate>
                  } />

                  {/* ── Trade Suggestions ────────────────────────────────────── */}
                  <Route path="/trade-suggestions" element={
                    <ScopeGate scope={TRADE_SUGGESTIONS} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <TradeSuggestionsList />
                      </div>
                    </ScopeGate>
                  } />

                  <Route path="/trade-suggestions/:isin" element={
                    <ScopeGate scope={TRADE_SUGGESTIONS} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <TradeSuggestionsDetail />
                      </div>
                    </ScopeGate>
                  } />

                  {/* ── Performance ──────────────────────────────────────────── */}
                  <Route path="/portfolio-performance" element={
                    <ScopeGate scope={PERFORMANCE} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <PortfolioPerformance />
                      </div>
                    </ScopeGate>
                  } />

                  <Route path="/stockwise-performance" element={
                    <ScopeGate scope={PERFORMANCE} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <StockwisePerformance />
                      </div>
                    </ScopeGate>
                  } />

                  <Route path="/clientwise-performance" element={
                    <ScopeGate scope={PERFORMANCE} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <ClientPerformance />
                      </div>
                    </ScopeGate>
                  } />

                  {/* ── Portfolio Transactions ───────────────────────────────── */}
                  <Route path="/portfolio-transactions" element={
                    <ScopeGate scope={PORTFOLIO_TRANSACTIONS} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <PortfolioTransaction />
                      </div>
                    </ScopeGate>
                  } />

                  {/* ── Distributor Contribution ─────────────────────────────── */}
                  <Route path="/distributor-contribution" element={
                    <ScopeGate scope={FEES_AND_DISTRIBUTOR} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <DistributorContribution />
                      </div>
                    </ScopeGate>
                  } />

                  {/* Unscoped specific analytics (market data staleness) */}
                  <Route path="/market-data-staleness" element={
                    <div className="App">
                      <Header />
                      <MarketDataStaleness />
                    </div>
                  } />

                  {/* ── Roles & User Management ──────────────────────────────── */}
                  <Route path="/roles-management" element={
                    <ScopeGate scope={USER_ROLE_MANAGEMENT} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <RolesManagement />
                      </div>
                    </ScopeGate>
                  } />
                  <Route path="/roles/new" element={
                      <ScopeGate scope={USER_ROLE_MANAGEMENT} fallback={<AccessDenied />}>
                    <div className="App">
                      <Header />
                      <RoleForm />
                    </div>
                    </ScopeGate>
                  } />
                  <Route path="/roles/:id/edit" element={
                    <ScopeGate scope={USER_ROLE_MANAGEMENT} fallback={<AccessDenied />}>
                    <div className="App">
                      <Header />
                      <RoleForm />
                    </div>
                    </ScopeGate>
                  } />

                  {/* User Management routes */}
                  <Route path="/users" element={
                   <ScopeGate scope={USER_ROLE_MANAGEMENT} fallback={<AccessDenied />}>
                    <div className="App">
                      <Header />
                      <UserList />
                    </div>
                    </ScopeGate>
                  } />
                  <Route path="/users/new" element={
                     <ScopeGate scope={USER_ROLE_MANAGEMENT} fallback={<AccessDenied />}>
                    <div className="App">
                      <Header />
                      <UserForm />
                    </div>
                    </ScopeGate>
                  } />
                  <Route path="/users/:id/edit" element={
                   <ScopeGate scope={USER_ROLE_MANAGEMENT} fallback={<AccessDenied />}>
                    <div className="App">
                      <Header />
                      <UserForm />
                    </div>
                    </ScopeGate>
                  } />

                  {/* Settings — superuser only (API enforces restriction) */}
                  <Route path="/settings" element={
                    <div className="App">
                      <Header />
                      <SettingsMaster />
                    </div>
                  } />

                  <Route path="/cash-environment" element={
                     <ScopeGate scope={ORDERS_MANAGEMENT} fallback={<AccessDenied />}>
                    <div className="App">
                      <Header />
                      <CashEnvironment />
                    </div>
                    </ScopeGate>
                  } />

                  <Route path="/management-fees" element={
                   <ScopeGate scope={FEES_AND_DISTRIBUTOR} fallback={<AccessDenied />}>
                    <div className="App">
                      <Header />
                      <ManagementFees />
                    </div>
                    </ScopeGate>
                  } />
                  <Route path="/management-fees/:clientCode" element={
                     <ScopeGate scope={FEES_AND_DISTRIBUTOR} fallback={<AccessDenied />}>
                    <div className="App">
                      <Header />
                      <ManagementFeeDetail />
                    </div>
                    </ScopeGate>
                  } />

                  <Route path="/sell/validation" element={
                   <ScopeGate scope={FINANCIAL_MANAGEMENT} fallback={<AccessDenied />}>
                    <div className="App">
                      <Header />
                      <SellValidation />
                    </div>
                    </ScopeGate>
                  } />

                  <Route path="/risk-calculation" element={
                   <ScopeGate scope={HOLDINGS_AND_DATA} fallback={<AccessDenied />}>
                    <div className="App">
                      <Header />
                      <RiskCalculation />
                    </div>
                    </ScopeGate>
                  }/>

                  <Route path="/contract-notes-process" element={
                   <ScopeGate scope={HOLDINGS_AND_DATA} fallback={<AccessDenied />}>
                    <div className="App">
                      <Header />
                      <ContractNoteProcess />
                    </div>
                    </ScopeGate>
                  } />

                  <Route path="/contract-files-manage" element={
                   <ScopeGate scope={HOLDINGS_AND_DATA} fallback={<AccessDenied />}>
                    <div className="App">
                      <Header />
                      <ContractFilesManage />
                    </div>
                    </ScopeGate>
                  } />

                  <Route path="/sector-holdings" element={
                   <ScopeGate scope={ANALYTICS} fallback={<AccessDenied />}>
                    <div className="App">
                      <Header />
                      <SectorWeightPage />
                    </div>
                    </ScopeGate>
                  } />

                  {/* ── Portfolio Q&A ────────────────────────────────────────── */}
                  <Route path="/qa" element={
                    <ScopeGate scope={PORTFOLIO_QA} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <QAPage />
                      </div>
                    </ScopeGate>
                  } />

                  {/* ── Operations Checklist ─────────────────────────────────── */}
                  <Route path="/operations-checklist" element={
                    <ScopeGate scope={OPERATIONS_CHECKLIST} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <OperationsChecklistPage />
                      </div>
                    </ScopeGate>
                  } />

                  <Route path="/operations-checklist/history" element={
                    <ScopeGate scope={OPERATIONS_CHECKLIST} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <OperationsHistoryPage />
                      </div>
                    </ScopeGate>
                  } />

                  <Route path="/operations-checklist/steps/:stepId" element={
                    <ScopeGate scope={OPERATIONS_CHECKLIST} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <StepDetailPage />
                      </div>
                    </ScopeGate>
                  } />

                  <Route path="/operations-checklist/:id" element={
                    <ScopeGate scope={OPERATIONS_CHECKLIST} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <ChecklistDetailPage />
                      </div>
                    </ScopeGate>
                  } />

                  {/* ── Kite Connect ─────────────────────────────────────────── */}
                  <Route path="/kite-connect" element={
                    <ScopeGate scope={KITE_CONNECT} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <KiteConnect />
                      </div>
                    </ScopeGate>
                  } />

                  {/* Unscoped routing specific (Not in ROUTE_SCOPES mapping) */}
                  <Route path="/order-generation-failures" element={
                    <div className="App">
                      <Header />
                      <OrderGenerationFailures />
                    </div>
                  } />

                  <Route path="/order-recommendation-list" element={
                    <ScopeGate scope={ORDERS_MANAGEMENT} fallback={<AccessDenied />}>
                      <div className="App">
                        <Header />
                        <OrderRecommendationList />
                      </div>
                    </ScopeGate>
                  } />
                  {/* Catch all -> Safely bounces back to dashboard */}
                  <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Route>
              </Routes>
            </Router>
          </NotificationProvider>
        </LaunchDarklyWrapper>
      </AuthProvider>
      <ToastContainer position="top-right" autoClose={3000} />
    </ThemeProvider>
  );
}

export default App;
